import type { ReactElement, FC, ReactNode } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { SpecialtyBranch } from '@provider-directory/app/ui/sceneFlows';
import { useOrgContextBasedOnHospitalAssociations } from '@provider-directory/components/org-context/org-context-provider';
import { useDecisionTreeMemory } from '@provider-directory/hooks/useDecisionTreeMemory';
import {
  DECISION_TREE_INITIAL_STEP,
  DecisionTreeMode,
  DecisionTreeSceneSlug,
  getDecisionTreeSpecialty,
} from '@provider-directory/models/decision-tree.model';
import { ProviderResource } from '@provider-directory/types/providers.contracts';
import { useFeatureFlags } from './FlagContext';
import { useSharedDecisionTree } from './SharedDecisionTreeContext';

// Module-level scheduling flow type tracker — set by BookAnotherProvider /
// RFV-mismatch buttons, read by dtree analytics. "Sticky" flag prevents the
// close effect from resetting the value when the user navigates away (e.g.
// "View All Providers") so the next dtree open still reads the correct type.
let _schedulingFlowType: 'FullScheduling' | 'BookAnotherProvider' =
  'FullScheduling';
let _schedulingFlowTypeSticky = false;
export const setSchedulingFlowType = (
  type: 'FullScheduling' | 'BookAnotherProvider',
  options?: { sticky?: boolean },
): void => {
  _schedulingFlowType = type;
  if (options?.sticky) {
    _schedulingFlowTypeSticky = true;
  }
};
export const getSchedulingFlowType = ():
  | 'FullScheduling'
  | 'BookAnotherProvider' => _schedulingFlowType;

// Module-level mirror of currentScene WHILE the dtree modal is open. Read by
// AnalyticsProvider's enrichWithSequence to attach questionId to events fired
// from inside the modal (e.g. CLICKED_EHR_REDIRECT on a dtree error scene).
// Null whenever the modal is closed. Same pattern as _schedulingFlowType to
// keep cross-context wiring consistent (AnalyticsProvider sits above
// DecisionTreeProvider in the tree, so React context is not available).
let _activeDtreeQuestionId: string | null = null;
export const setActiveDtreeQuestionId = (scene: string | null): void => {
  _activeDtreeQuestionId = scene;
};
export const getActiveDtreeQuestionId = (): string | null =>
  _activeDtreeQuestionId;

interface DecisionTreeBase {
  rfvKey: string;
  rfvId: string;
}

export type DecisionTreeType = DecisionTreeBase &
  Record<string, string | undefined>;

export type PartialDecisionTreeType = Partial<DecisionTreeType>;

export const DEFAULT_DECISION_TREE: DecisionTreeType = {
  rfvKey: '',
  rfvId: '',
  mode: DecisionTreeMode.Normal,
};

export interface DecisionTreeContextType {
  decisionTree: DecisionTreeType;
  specialtyBranch?: SpecialtyBranch;
  currentScene: DecisionTreeSceneSlug;
  navigationHistory: DecisionTreeSceneSlug[];
  applyHistoryAndActivate: (
    history: DecisionTreeSceneSlug[],
    activeScene: DecisionTreeSceneSlug,
  ) => void;
  updateDecisionTree: (patch: PartialDecisionTreeType) => DecisionTreeType;
  resetDecisionTree: (patch?: PartialDecisionTreeType) => DecisionTreeType;
  setCurrentScene: (scene: DecisionTreeSceneSlug) => void;
  navigateToScene: (scene: DecisionTreeSceneSlug) => void;
  navigateBack: () => boolean;
  canNavigateBack: boolean;
  mode: DecisionTreeMode;
  setMode: (mode: DecisionTreeMode) => void;
  setMismatchProviderOverride: (provider: ProviderResource | null) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isRestoringMemory: boolean;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
}

const DecisionTreeContext = createContext<DecisionTreeContextType | null>(null);

const DecisionTreeProvider: FC<{
  children: ReactNode;
  provider: ProviderResource | null;
}> = ({ children, provider }): ReactElement => {
  const [overrideProvider, setOverrideProvider] =
    useState<ProviderResource | null>(null);
  const activeProvider = overrideProvider ?? provider;
  const { decisionTree: decisionTreeConfig } =
    useOrgContextBasedOnHospitalAssociations(
      activeProvider?.hospitalAssociations ?? provider?.hospitalAssociations,
    );
  const { featureFlags } = useFeatureFlags();
  const {
    sharedData,
    updateSharedData,
    updateLastViewedScene,
    updateNavigationHistory,
    getInitialScene,
  } = useSharedDecisionTree();

  const { computeHydration } = useDecisionTreeMemory();
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [decisionTree, setDecisionTree] = useState<DecisionTreeType>(
    DEFAULT_DECISION_TREE,
  );
  const [mode, setMode] = useState<DecisionTreeMode>(DecisionTreeMode.Normal);
  const [activeHistoryIndex, setActiveHistoryIndex] = useState<number>(-1);
  const [isRestoringMemory, setIsRestoringMemory] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const specialtyBranchName = getDecisionTreeSpecialty({
    specialtyMap: decisionTreeConfig.specialtyMap,
    provider: activeProvider ?? undefined,
    featureFlags,
  });

  const decisionTreeAnswersUsage = decisionTreeConfig.decisionTreeAnswersUsage;

  const initialScene = getInitialScene(
    specialtyBranchName,
    decisionTreeAnswersUsage,
  ) as DecisionTreeSceneSlug;

  const [currentScene, setCurrentScene] =
    useState<DecisionTreeSceneSlug>(initialScene);
  const [navigationHistory, setNavigationHistory] = useState<
    DecisionTreeSceneSlug[]
  >([]);

  const isActiveScene = useCallback(
    (scene: DecisionTreeSceneSlug) =>
      activeHistoryIndex >= 0 &&
      navigationHistory[activeHistoryIndex] === scene,
    [activeHistoryIndex, navigationHistory],
  );

  const persistMemory = useCallback(
    (history: DecisionTreeSceneSlug[], scene: DecisionTreeSceneSlug) => {
      if (specialtyBranchName) {
        updateLastViewedScene(scene);
        updateNavigationHistory(history);
      }
    },
    [specialtyBranchName, updateLastViewedScene, updateNavigationHistory],
  );

  // Returns the base history up to and including the active index.
  const getBaseHistory = useCallback(
    (history: DecisionTreeSceneSlug[], idx: number) =>
      idx >= 0 ? history.slice(0, idx + 1) : history,
    [],
  );

  const updateDecisionTree = useCallback(
    (patch: PartialDecisionTreeType) => {
      const newDecisionTree: DecisionTreeType = {
        ...decisionTree,
        ...patch,
      };
      setDecisionTree(newDecisionTree);

      // Keep shared memory in sync with RFV if enabled
      if (specialtyBranchName) {
        updateSharedData({
          specialtyBranch: specialtyBranchName,
          reasonForVisitId: patch.rfvId ?? decisionTree.rfvId,
          reasonForVisitKey: patch.rfvKey ?? decisionTree.rfvKey,
          providerId: provider?.npi ?? null,
        });
      }

      return newDecisionTree;
    },
    [decisionTree, specialtyBranchName, updateSharedData, provider?.npi],
  );

  const resetDecisionTree = useCallback(
    (newDecisionTreeData?: PartialDecisionTreeType) => {
      const newDecisionTree = {
        ...DEFAULT_DECISION_TREE,
        ...newDecisionTreeData,
      };
      setDecisionTree(newDecisionTree);
      setCurrentScene(DECISION_TREE_INITIAL_STEP);
      setNavigationHistory([]);
      setActiveHistoryIndex(-1);
      return newDecisionTree;
    },
    [],
  );

  const applyHistoryAndActivate = useCallback(
    (history: DecisionTreeSceneSlug[], activeScene: DecisionTreeSceneSlug) => {
      let nextHistory = history;
      if (!history.includes(activeScene)) {
        nextHistory = [...history, activeScene];
      }

      setNavigationHistory(nextHistory);
      const idx = nextHistory.indexOf(activeScene);
      setActiveHistoryIndex(idx);
      setCurrentScene(activeScene);

      persistMemory(nextHistory, activeScene);
    },
    [persistMemory],
  );

  // Wrapper for setCurrentScene that also syncs memory
  const handleSetCurrentScene = useCallback(
    (scene: DecisionTreeSceneSlug) => {
      setCurrentScene(scene);

      if (isActiveScene(scene)) {
        persistMemory(navigationHistory, scene);
        return;
      }

      const existingIndex = navigationHistory.indexOf(scene);
      if (existingIndex >= 0) {
        setActiveHistoryIndex(existingIndex);
        persistMemory(navigationHistory, scene);
        return;
      }

      const base = getBaseHistory(navigationHistory, activeHistoryIndex);
      const nextHistory = [...base, scene];

      setNavigationHistory(nextHistory);
      setActiveHistoryIndex(nextHistory.length - 1);

      persistMemory(nextHistory, scene);
    },
    [
      activeHistoryIndex,
      navigationHistory,
      isActiveScene,
      getBaseHistory,
      persistMemory,
      setCurrentScene,
    ],
  );

  const navigateToScene = useCallback(
    (scene: DecisionTreeSceneSlug) => {
      setCurrentScene(scene);

      if (isActiveScene(scene)) {
        persistMemory(navigationHistory, scene);
        return;
      }

      const base = getBaseHistory(navigationHistory, activeHistoryIndex);

      // no-op if already the last scene
      if (base.length > 0 && base[base.length - 1] === scene) {
        setActiveHistoryIndex(base.length - 1);
        persistMemory(base, scene);
        return;
      }

      const nextHistory = [...base, scene];
      setNavigationHistory(nextHistory);
      setActiveHistoryIndex(nextHistory.length - 1);

      persistMemory(nextHistory, scene);
    },
    [
      activeHistoryIndex,
      navigationHistory,
      isActiveScene,
      getBaseHistory,
      persistMemory,
      setCurrentScene,
    ],
  );

  const navigateBack = useCallback((): boolean => {
    if (activeHistoryIndex <= 0) return false;

    const newIndex = activeHistoryIndex - 1;
    const prevScene = navigationHistory[newIndex];

    setActiveHistoryIndex(newIndex);
    setCurrentScene(prevScene);

    persistMemory(navigationHistory, prevScene);

    return true;
  }, [activeHistoryIndex, navigationHistory, persistMemory, setCurrentScene]);

  const canNavigateBack = activeHistoryIndex > 0;

  const prevIsOpenForHydrationRef = useRef(false);
  useEffect(() => {
    // Only run hydration when the modal opens (transition from closed to open)
    // This prevents hydration from overriding active navigation within the modal
    const justOpened = isOpen && !prevIsOpenForHydrationRef.current;
    prevIsOpenForHydrationRef.current = isOpen;

    if (!justOpened) return;

    if (!specialtyBranchName) {
      setIsRestoringMemory(false);
      return;
    }

    const plan = computeHydration({
      shared: {
        specialtyBranch: sharedData.specialtyBranch,
        answers: sharedData.answers,
        navigationHistory: sharedData.navigationHistory,
        lastViewedScene: sharedData.lastViewedScene || null,
        providerId: sharedData.providerId,
        overrideProviderId: sharedData.overrideProviderId,
      },
      specialtyBranch: specialtyBranchName,
      answersUsage: decisionTreeAnswersUsage,
      currentProviderId: provider?.npi ?? null,
    });

    // Clear the override provider if we're returning to the original provider
    if (plan.shouldClearOverride) {
      setOverrideProvider(null);
      updateSharedData({
        overrideProviderId: null,
        navigationHistory: plan.normalizedHistory,
        lastViewedScene: plan.resumeScene,
      });
    }

    if (plan.isSpecialtyChanged) {
      updateSharedData({
        specialtyBranch: specialtyBranchName,
        lastViewedScene: DECISION_TREE_INITIAL_STEP,
        navigationHistory: [DECISION_TREE_INITIAL_STEP],
        reasonForVisitId: null,
        reasonForVisitKey: null,
        answers: {},
        providerId: provider?.npi ?? null,
      });
      // Reset the local decision tree state
      resetDecisionTree();
      // Apply the initial state
      applyHistoryAndActivate(
        [DECISION_TREE_INITIAL_STEP],
        DECISION_TREE_INITIAL_STEP,
      );
      setIsRestoringMemory(false);
      return;
    }

    // Update decision tree with saved answers
    setDecisionTree((prev) => ({
      ...prev,
      ...sharedData.answers,
      rfvId: sharedData.reasonForVisitId || prev.rfvId,
      rfvKey: sharedData.reasonForVisitKey || prev.rfvKey,
    }));

    // Sync navigation history if it changed
    if (
      sharedData.navigationHistory.join('|') !==
      plan.normalizedHistory.join('|')
    ) {
      updateSharedData({ navigationHistory: plan.normalizedHistory });
    }

    applyHistoryAndActivate(plan.normalizedHistory, plan.resumeScene);
    setIsRestoringMemory(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, specialtyBranchName]);

  // Reset restoration flag and scheduling flow type when modal opens/closes.
  // On open: consume the sticky flag (value is kept as-is).
  // On close: reset to FullScheduling unless sticky (cross-page navigation).
  useEffect(() => {
    if (isOpen) {
      _schedulingFlowTypeSticky = false;
    } else {
      setIsRestoringMemory(true);
      if (!_schedulingFlowTypeSticky) {
        _schedulingFlowType = 'FullScheduling';
      }
    }
  }, [isOpen]);

  // Mirror the current scene into module-level state while the modal is open,
  // so AnalyticsProvider can attach questionId to events fired from inside the
  // dtree UI (CLICKED_EHR_REDIRECT, CLICKED_TO_CALL). Cleared on close and on
  // provider unmount so the mirror never outlives an open modal.
  useEffect(() => {
    setActiveDtreeQuestionId(isOpen ? currentScene : null);
    return () => {
      setActiveDtreeQuestionId(null);
    };
  }, [isOpen, currentScene]);

  const setMismatchProviderOverride = useCallback(
    (nextProvider: ProviderResource | null) => {
      setOverrideProvider(nextProvider);
      updateSharedData({ overrideProviderId: nextProvider?.npi ?? null });
    },
    [updateSharedData],
  );

  const prevIsOpenRef = useRef(isOpen);
  useEffect(() => {
    if (prevIsOpenRef.current && !isOpen && overrideProvider) {
      setMismatchProviderOverride(null);

      if (
        navigationHistory[navigationHistory.length - 1] === 'select-time-modal'
      ) {
        const trimmed = navigationHistory.slice(0, -1);
        const fallbackHistory =
          trimmed.length > 0 ? trimmed : [DECISION_TREE_INITIAL_STEP];
        const targetScene = fallbackHistory[fallbackHistory.length - 1];
        applyHistoryAndActivate(fallbackHistory, targetScene);
      }
    }
    prevIsOpenRef.current = isOpen;
  }, [
    isOpen,
    applyHistoryAndActivate,
    navigationHistory,
    overrideProvider,
    setMismatchProviderOverride,
  ]);

  // Initial hydration effect (runs once on mount)
  useEffect(() => {
    // Only hydrate once
    if (navigationHistory.length > 0) return;

    if (!specialtyBranchName) {
      const initial: DecisionTreeSceneSlug[] = [initialScene];
      setNavigationHistory(initial);
      setActiveHistoryIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset state when URL changes
  const previousUrlRef = useRef<string>('');
  useEffect(() => {
    const currentUrl = `${pathname}?${searchParams.toString()}`;
    if (
      currentUrl !== previousUrlRef.current &&
      previousUrlRef.current !== ''
    ) {
      resetDecisionTree();
      setMode(DecisionTreeMode.Normal);
      setIsOpen(false);
      setIsLoading(false);
    }
    previousUrlRef.current = currentUrl;
  }, [pathname, searchParams, resetDecisionTree]);

  return (
    <DecisionTreeContext.Provider
      value={{
        decisionTree,
        specialtyBranch: specialtyBranchName ?? 'pediatrics',
        currentScene,
        navigationHistory,
        applyHistoryAndActivate,
        updateDecisionTree,
        resetDecisionTree,
        setCurrentScene: handleSetCurrentScene,
        navigateToScene,
        navigateBack,
        canNavigateBack,
        mode,
        setMode,
        setMismatchProviderOverride,
        isOpen,
        setIsOpen,
        isRestoringMemory,
        isLoading,
        setIsLoading,
      }}
    >
      {children}
    </DecisionTreeContext.Provider>
  );
};

export const useDecisionTree = (): DecisionTreeContextType => {
  const decisionTreeContext = useContext(DecisionTreeContext);

  if (!decisionTreeContext) {
    throw new Error(
      'useDecisionTree must be used within <DecisionTreeContext.Provider>',
    );
  }
  return decisionTreeContext;
};

export { DecisionTreeProvider, DecisionTreeContext };
